import React, { useState, useContext, useEffect, useMemo, useCallback, useRef } from 'react';
import { LanguageContext } from '../context/LanguageContext';
import templateTranslations from '../languages/template.json';
import { useAuth } from '../hooks/AuthProvider';
import { AddNewIcon, DeleteIcon } from '../svg/UtilsIcons';
import { AddEvaluationsIcon, CurrentEvaluationsIcon } from '../svg/TemplateIcons';
import Alert from '../Components/Alert';
import AppDialog from '../Components/AppDialog';
import ColorPicker from '../Components/ColorPicker';

import '../Components/modal.css';
import './EditTemplateEvaluations.css';

let newIdCounter = -1;

export const EditTemplateEvaluations = ({
  state,
  setState,
  templateEvaluationData,
  setTemplateEvaluationData
}) => {
  const { lang } = useContext(LanguageContext);
  const t = templateTranslations[lang].TemplateDetails;
  const isRtl = ['ar', 'ur'].includes(lang);

  const { permissions } = useAuth();
  const canEditTemplate = permissions['edit_templates'];

  const sortedEvaluations = useMemo(
    () =>
      (templateEvaluationData.evaluation_names || [])
        .map((e) => {
          const rawLight = e.colorLight ?? e.color_light ?? null;
          const resolvedLight =
            rawLight != null && String(rawLight).trim() !== ''
              ? rawLight
              : (e.color ?? null);
          return { ...e, colorLight: resolvedLight };
        })
        .slice()
        .sort(
          (a, b) =>
            (a.priority || 100) - (b.priority || 100) ||
            a.name.localeCompare(b.name)
        ),
    [templateEvaluationData.evaluation_names]
  );

  const [localEvaluations, setLocalEvaluations] = useState(sortedEvaluations);
  const [newName, setNewName] = useState('');
  const [newPriority, setNewPriority] = useState(1);
  const [newColor, setNewColor] = useState('#00C0C7');
  const [newColorLight, setNewColorLight] = useState('#00C0C7');

  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    type: 'info',
    position: isRtl ? 'left' : 'right'
  });

  const showAlert = useCallback(
    (type, message) => {
      setAlertState({
        open: true,
        type,
        message,
        position: isRtl ? 'left' : 'right'
      });
    },
    [isRtl]
  );

  const resolverRef = useRef(null);

  const [dialog, setDialog] = useState({
    open: false,
    type: 'confirm',
    variant: 'danger',
    icon: 'delete',
    title: '',
    message: '',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    showCancel: true
  });

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;

      setDialog({
        open: true,
        type: opts?.type || 'confirm',
        variant: opts?.variant || 'danger',
        icon: opts?.icon || 'delete',
        title: opts?.title || '',
        message: opts?.message || '',
        confirmText: opts?.confirmText || 'Ok',
        cancelText: opts?.cancelText || 'Cancel',
        showCancel: Boolean(opts?.showCancel)
      });
    });
  }, []);

  const closeDialog = useCallback((result) => {
    setDialog((d) => ({ ...d, open: false }));
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  const closeDialogAsCancel = useCallback(() => closeDialog(false), [closeDialog]);
  const handleDialogConfirm = useCallback(() => closeDialog(true), [closeDialog]);
  const handleDialogCancel = useCallback(() => closeDialog(false), [closeDialog]);

  useEffect(() => {
    if (state) {
      setLocalEvaluations(sortedEvaluations);

      const maxPriority = sortedEvaluations.reduce((max, e) => {
        const p = parseInt(e?.priority);
        return Number.isFinite(p) ? Math.max(max, p) : max;
      }, 0);

      setNewPriority(maxPriority > 0 ? maxPriority + 1 : 1);

      const firstColor = (sortedEvaluations.find((e) => e.color)?.color) || '#00C0C7';
      const firstColorLight =
        (sortedEvaluations.find((e) => e.colorLight)?.colorLight) || firstColor;
      setNewColor(firstColor);
      setNewColorLight(firstColorLight);
    }
  }, [state, sortedEvaluations]);

  const handleAddEvaluation = () => {
    if (!canEditTemplate) return;

    const trimmedName = newName.trim();
    if (!trimmedName) return;

    const priorityValue = parseInt(newPriority) || 100;

    if (localEvaluations.some((e) => e.name.toLowerCase() === trimmedName.toLowerCase())) {
      showAlert('error', t.evaluations.nameExists);
      return;
    }

    const newEval = {
      template_datastream_evaluation_id: newIdCounter--,
      name: trimmedName,
      priority: priorityValue,
      color: newColor || '#00C0C7',
      colorLight: newColorLight || newColor || '#00C0C7',
    };

    setLocalEvaluations((prev) =>
      [...prev, newEval].sort(
        (a, b) =>
          (a.priority || 100) - (b.priority || 100) ||
          a.name.localeCompare(b.name)
      )
    );

    setNewName('');
    setNewPriority(priorityValue + 1);
  };

  const handleEditField = (id, field, value) => {
    if (!canEditTemplate) return;

    setLocalEvaluations((prev) =>
      prev
        .map((e) => {
          if (e.template_datastream_evaluation_id === id) {
            if (field === 'name') {
              const trimmedName = value.trim();

              if (
                prev.some(
                  (item) =>
                    item.name.toLowerCase() === trimmedName.toLowerCase() &&
                    item.template_datastream_evaluation_id !== id
                )
              ) {
                showAlert('error', t.evaluations.nameExists);
                return e;
              }

              return { ...e, name: trimmedName };
            }

            if (field === 'priority') {
              const priorityValue = parseInt(value);
              return { ...e, priority: isNaN(priorityValue) ? null : priorityValue };
            }

            if (field === 'color') {
              return { ...e, color: value || null };
            }

            if (field === 'colorLight') {
              return { ...e, colorLight: value || null };
            }
          }
          return e;
        })
        .sort(
          (a, b) =>
            (a.priority || 100) - (b.priority || 100) ||
            a.name.localeCompare(b.name)
        )
    );
  };

  const handleDeleteEvaluation = async (id) => {
    if (!canEditTemplate) return;

    const ok = await confirm({
      type: 'confirm',
      variant: 'danger',
      icon: 'delete',
      title: t.evaluations.deleteTitle || 'Delete evaluation?',
      message: t.evaluations.deleteWarning,
      confirmText: t.evaluations.deleteConfirmText || 'Delete',
      cancelText: t.cancel || 'Cancel',
      showCancel: true
    });

    if (!ok) return;

    setLocalEvaluations((prev) =>
      prev.filter((e) => e.template_datastream_evaluation_id !== id)
    );
  };

  const handleApply = () => {
    if (!canEditTemplate) return;

    const deletedIds = templateEvaluationData.evaluation_names
      .map((e) => e.template_datastream_evaluation_id)
      .filter(
        (id) =>
          !localEvaluations.some(
            (le) => le.template_datastream_evaluation_id === id && id > 0
          )
      );

    const updatedRanges = templateEvaluationData.datastream_ranges.filter(
      (range) => !deletedIds.includes(range.template_datastream_evaluation_id)
    );

    setTemplateEvaluationData({
      evaluation_names: localEvaluations.map((e) => ({
        ...e,
        template_datastream_evaluation_id:
          e.template_datastream_evaluation_id != null
            ? e.template_datastream_evaluation_id
            : null,
        priority: e.priority != null ? e.priority : 100,
        color: e.color || null,
        colorLight: e.colorLight || e.color || null,
      })),
      datastream_ranges: updatedRanges
    });

    setState(false);
  };

  const handleBackdropClick = () => {
    if (dialog.open) return;
    setState(false);
  };

  if (!state) return null;

  return (
    <div
      id="modal"
      className={`ete-modal-root ${isRtl ? 'rtl' : 'ltr'}`}
      onClick={handleBackdropClick}
    >
      <Alert
        open={alertState.open}
        close={() => setAlertState({ ...alertState, open: false })}
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />

      <div
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <AppDialog
          open={dialog.open}
          type={dialog.type}
          variant={dialog.variant}
          icon={dialog.icon}
          title={dialog.title}
          message={dialog.message}
          confirmText={dialog.confirmText}
          cancelText={dialog.cancelText}
          showCancel={dialog.showCancel}
          onConfirm={handleDialogConfirm}
          onCancel={handleDialogCancel}
          onClose={closeDialogAsCancel}
        />
      </div>

      <div
        id="popup"
        className="dashboard-modal widget-settings-modal ete-popup"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dashboard-header">
          <div className="dashboard-header-icon">
            <AddNewIcon />
          </div>

          <div className="dashboard-header-text">
            <h2>{t.evaluations.title}</h2>
            <p>{t.evaluations.subTitle || 'Edit your Template Evaluations details'}</p>
          </div>
        </div>

        <div className="modal-divider" />

        <div className="modal-content dashboard-content">
          <div className="ete-info-box">
            <button type="button" className="ete-info-badge">
              <span className="ete-info-badge-icon">
                <AddEvaluationsIcon />
              </span>
              <span>{t.evaluations.addTextLabel || 'New Evaluation Description'}</span>
            </button>

            <div className="ete-info-row">
              <span className="ete-warning-dot">!</span>
              <div className="ete-info-copy">
                <p className="ete-info-paragraph">{t.evaluations.infoMessage}</p>
                <p className="ete-info-paragraph ete-info-paragraph--accent">
                  {t.evaluations.priorityInfo}
                </p>
              </div>
            </div>
          </div>

          <div className="ete-section-title">
            <AddEvaluationsIcon color="var(--ete-accent)" />
            <span className="ete-section-title-text">
              {t.evaluations.addSectionTitle || 'Add New Evaluation'}
            </span>
          </div>

          {canEditTemplate && (
            <div className="ete-add-grid">
              <div className="ete-field">
                <span className="ete-label">
                  {t.evaluations.placeholderNameLabel || 'Evaluation Name'}
                </span>
                <input
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder={t.evaluations.placeholderName}
                  maxLength={50}
                  className="add-column ete-input"
                />
              </div>

              <div className="ete-field">
                <span className="ete-label">
                  {t.evaluations.labelPriority || 'Numeric ID'}
                </span>
                <input
                  type="number"
                  value={newPriority}
                  onChange={e => setNewPriority(e.target.value)}
                  placeholder="e.g., 104"
                  min={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleAddEvaluation();
                  }}
                  className="add-column ete-input"
                />
              </div>

              <div className="ete-field">
                <span className="ete-label">
                  {t.evaluations.labelColorDark || t.evaluations.labelColor || 'Color (Dark)'}
                </span>
                <div className="ete-color-field">
                  <ColorPicker id="new-eval-color" color={newColor} setColor={setNewColor} />
                </div>
              </div>

              <div className="ete-field">
                <span className="ete-label">
                  {t.evaluations.labelColorLight || 'Color (Light)'}
                </span>
                <div className="ete-color-field">
                  <ColorPicker
                    id="new-eval-color-light"
                    color={newColorLight}
                    setColor={setNewColorLight}
                  />
                </div>
              </div>

              <div className="ete-field ete-field--button">
                <button
                  type="button"
                  onClick={handleAddEvaluation}
                  className="button btn-apply ete-add-btn"
                >
                  {t.evaluations.buttonAdd}
                </button>
              </div>
            </div>
          )}

          <div className="ete-section-title">
            <CurrentEvaluationsIcon color="var(--ete-accent)" />
            <span className="ete-section-title-text">
              {t.evaluations.currentTitle || 'Current Evaluations'}
            </span>
          </div>

          <div className="ete-list-box">
            {localEvaluations.length === 0 ? (
              <div className="ete-empty">
                {t.evaluations.emptyText || 'No evaluations added yet. Add your first evaluation above.'}
              </div>
            ) : (
              <ul className="ete-list">
                {localEvaluations.map((evaluation) => (
                  <li
                    key={evaluation.template_datastream_evaluation_id}
                    className="ete-row"
                  >
                    <input
                      type="text"
                      value={evaluation.name}
                      onChange={(e) =>
                        handleEditField(
                          evaluation.template_datastream_evaluation_id,
                          'name',
                          e.target.value
                        )
                      }
                      disabled={!canEditTemplate}
                      maxLength={50}
                      className="ete-row-input ete-row-input--name"
                    />

                    <input
                      type="number"
                      value={evaluation.priority ?? ''}
                      onChange={(e) =>
                        handleEditField(
                          evaluation.template_datastream_evaluation_id,
                          'priority',
                          e.target.value
                        )
                      }
                      disabled={!canEditTemplate}
                      min={0}
                      className="ete-row-input ete-row-input--priority"
                    />

                    <div className="ete-row-color">
                      <ColorPicker
                        id={`eval-color-${evaluation.template_datastream_evaluation_id}`}
                        color={evaluation.color || '#00C0C7'}
                        setColor={(c) =>
                          handleEditField(
                            evaluation.template_datastream_evaluation_id,
                            'color',
                            c
                          )
                        }
                        pickerClassName="color-picker"
                      />
                    </div>

                    <div className="ete-row-color">
                      <ColorPicker
                        id={`eval-color-light-${evaluation.template_datastream_evaluation_id}`}
                        color={evaluation.colorLight || evaluation.color || '#00C0C7'}
                        setColor={(c) =>
                          handleEditField(
                            evaluation.template_datastream_evaluation_id,
                            'colorLight',
                            c
                          )
                        }
                        pickerClassName="color-picker"
                      />
                    </div>

                    <div className="ete-row-actions">
                      {canEditTemplate && (
                        <button
                          type="button"
                          onClick={() => handleDeleteEvaluation(evaluation.template_datastream_evaluation_id)}
                          className="ete-delete-btn"
                        >
                          <DeleteIcon />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="modal-footer dashboard-footer ete-footer">
          <button className="btn btn-cancel" type="button" onClick={() => setState(false)}>
            {t.cancel}
          </button>

          {canEditTemplate && (
            <button className="btn btn-apply dashboard-primary-btn" type="button" onClick={handleApply}>
              {t.apply}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
